import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch, onMounted, onBeforeUnmount, onUpdated } from 'vue';
    import { Input } from '@/components/ui/input';
    import type { HTMLAttributes } from 'vue';
    import AutoNumeric from 'autonumeric';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'ThousandSeparatorInput',
  props: {
    modelValue: {},
    placeholder: {},
    class: {},
    withDecimals: { type: Boolean },
    hideZeroes: { type: Boolean }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

    const props = __props;

    const emits = __emit;

    // Template ref for the input element
    const inputRef = ref<InstanceType<typeof Input>>();
    let autoNumericInstance: AutoNumeric | null = null;

    // AutoNumeric configuration
    const autoNumericOptions = computed(() => ({
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalPlaces: props.withDecimals ? 2 : 0,
        decimalPlacesRawValue: props.withDecimals ? 2 : 0,
        allowDecimalPadding: true,
        emptyInputBehavior: 'null',
        selectOnFocus: false,
        // unformatOnSubmit: true,
        watchExternalChanges: true,
        modifyValueOnWheel: false,
    }));

    onMounted(() => {
        if (inputRef.value) {
            const domElement = inputRef.value.$el;

            if (domElement && domElement.tagName === 'INPUT') {
                try {
                    autoNumericInstance = new AutoNumeric(
                        domElement,
                        props.modelValue ?? '',
                        autoNumericOptions.value,
                    );

                    initializeValue(autoNumericInstance);

                    domElement.addEventListener('blur', handleInputChange);
                    domElement.addEventListener('keyup', handleInputChange);
                } catch (error) {
                    console.error('Failed to initialize AutoNumeric:', error);
                }
            }
        }
    });

    onUpdated(() => {
        if (autoNumericInstance) {
            const currentValue = autoNumericInstance.getNumber();

            if (props.modelValue !== currentValue) {
                try {
                    initializeValue(autoNumericInstance);
                } catch (error) {
                    console.error('Error setting AutoNumeric value:', error);
                }
            }
        }
    });

    const initializeValue = (autoNumericInstance: AutoNumeric) => {
        const isEmpty = [
            props.modelValue === undefined,
            props.modelValue === null,
            props.modelValue === '',
            ...(props.hideZeroes ? [props.modelValue === 0] : []),
        ].some(Boolean);

        if (isEmpty) {
            autoNumericInstance.clear();
        } else {
            autoNumericInstance.set(props.modelValue as string | number | null);
        }
    };

    onBeforeUnmount(() => {
        if (autoNumericInstance) {
            try {
                const domElement = inputRef.value?.$el;

                if (domElement && domElement.tagName === 'INPUT') {
                    domElement.removeEventListener('blur', handleInputChange);
                    domElement.removeEventListener('keyup', handleInputChange);
                }

                autoNumericInstance.remove();
            } catch (error) {
                console.error('Error during AutoNumeric cleanup:', error);
            } finally {
                autoNumericInstance = null;
            }
        }
    });

    const handleInputChange = () => {
        if (autoNumericInstance) {
            const domElement = inputRef.value?.$el;
            if (domElement && domElement.value === '') {
                emits('update:modelValue', null);
            } else {
                const rawValue = autoNumericInstance.getNumber();
                emits('update:modelValue', rawValue);
            }
        }
    };

    // Watch for changes to withDecimals prop and update configuration
    watch(
        () => props.withDecimals,
        () => {
            if (autoNumericInstance) {
                autoNumericInstance.update(autoNumericOptions.value);
            }
        },
    );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Input), _mergeProps({
    ref_key: "inputRef",
    ref: inputRef,
    placeholder: _ctx.placeholder,
    class: props.class
  }, _ctx.$attrs), null, 16, ["placeholder", "class"]))
}
}

})